import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  Calendar,
  MapPin,
  Download,
  Ticket,
  Search,
  CreditCard,
  Hash,
} from "lucide-react";
import UserAuth from "../Components/PrivateRoutes/UserAuth";
import MarketplaceNewUIFooter from "../Components/Home/MarketplaceNewUI/MarketplaceNewUIFooter";
import LatestLoading from "../Components/Loading/LatestLoading";
import { UserOrdersFunc, PrintOrder } from "../redux/actions/UserOrder";
import {
  filterTicketsBySearch,
  filterTicketsByTab,
  mapOrderToTicketCard,
} from "../Components/Home/MarketplaceNewUI/myTicketsHelpers";
import OrderQrTickets from "../Components/TicketScanner/OrderQrTickets";
import { ticketApi } from "../Components/TicketScanner/ticketApi";

function MyTicketsContent() {
  const dispatch = useDispatch();
  const userOrders = useSelector((state) => state.order.UserOrders);
  const isLoading = useSelector((state) => state.order.isloading);
  const [activeTab, setActiveTab] = useState("upcoming");
  const [search, setSearch] = useState("");

  const [qrByOrder, setQrByOrder] = useState({});

  useEffect(() => {
    dispatch(UserOrdersFunc());
  }, [dispatch]);

  // QR tickets for organizer-created events, grouped by order number
  useEffect(() => {
    let cancelled = false;
    ticketApi
      .myTickets()
      .then((data) => {
        if (cancelled) return;
        const grouped = {};
        for (const t of data.tickets || []) {
          const key = String(t.orderNumber);
          (grouped[key] = grouped[key] || []).push(t);
        }
        Object.values(grouped).forEach((list) => list.sort((a, b) => a.seatIndex - b.seatIndex));
        setQrByOrder(grouped);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const allTickets = useMemo(
    () =>
      (userOrders || [])
        .map(mapOrderToTicketCard)
        .filter(Boolean)
        .sort((a, b) => {
          const aTime = a.eventMoment?.valueOf() ?? new Date(a.purchasedAt).getTime();
          const bTime = b.eventMoment?.valueOf() ?? new Date(b.purchasedAt).getTime();
          return bTime - aTime;
        }),
    [userOrders]
  );

  const tabCounts = useMemo(
    () => ({
      upcoming: filterTicketsByTab(allTickets, "upcoming").length,
      past: filterTicketsByTab(allTickets, "past").length,
      transferred: filterTicketsByTab(allTickets, "transferred").length,
    }),
    [allTickets]
  );

  const tabs = [
    { id: "upcoming", label: "Upcoming", count: tabCounts.upcoming },
    { id: "past", label: "Past", count: tabCounts.past },
    { id: "transferred", label: "Transferred", count: tabCounts.transferred },
  ];

  const displayTickets = useMemo(() => {
    const byTab = filterTicketsByTab(allTickets, activeTab);
    return filterTicketsBySearch(byTab, search);
  }, [allTickets, activeTab, search]);

  const handleDownloadInvoice = (ticket) => {
    if (!ticket?.order_Id || ticket.ticket_group_id == null) return;
    dispatch(PrintOrder(ticket.order_Id, ticket.ticket_group_id));
  };

  return (
    <div className="min-h-screen bg-[#0F172A]">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10"
      >
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8"
        >
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
          >
            <h1 className="text-3xl font-black uppercase tracking-tight text-white">
              My Tickets
            </h1>
            <p className="text-white/40 text-sm mt-1">
              {allTickets.length} order{allTickets.length !== 1 ? "s" : ""} total
              {displayTickets.length !== allTickets.length
                ? ` · ${displayTickets.length} shown`
                : ""}
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="relative w-full sm:w-72"
          >
            <Search className="absolute left-3 top-1/3 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tickets..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-[#111827] border border-[#1F2937] text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-red-500/30"
            />
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
          className="flex flex-wrap gap-1 mb-8 bg-[#111827] p-1.5 rounded-xl border border-[#1F2937] w-fit"
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-black transition-all ${
                activeTab === tab.id
                  ? "bg-red-600 text-white shadow-lg shadow-red-600/20"
                  : "text-white/50 hover:text-white hover:bg-white/5"
              }`}
            >
              {tab.label}
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded-md ${
                  activeTab === tab.id ? "bg-white/20" : "bg-white/5"
                }`}
              >
                {tab.count}
              </span>
            </button>
          ))}
        </motion.div>

        {isLoading && allTickets.length === 0 ? (
          <div className="flex justify-center py-24">
            <LatestLoading />
          </div>
        ) : displayTickets.length > 0 ? (
          <div className="space-y-4">
            {displayTickets.map((ticket, idx) => (
              <motion.article
                key={ticket.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.06 }}
                className="bg-[#111827] rounded-2xl border border-[#1F2937] overflow-hidden hover:border-[#374151] transition-all"
              >
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: idx * 0.06 + 0.04 }}
                  className="p-6 sm:p-7"
                >
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.06 + 0.08 }}
                    className="flex flex-col gap-4"
                  >
                    <motion.div
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.06 + 0.1 }}
                      className="flex flex-wrap items-start justify-between gap-3"
                    >
                      <div className="min-w-0 flex-1">
                        <span className="inline-block px-2 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-wider bg-red-600/20 text-red-400 mb-2">
                          {ticket.type || "Ticket"}
                        </span>
                        <h2 className="text-lg sm:text-xl font-black uppercase tracking-tight text-white">
                          {ticket.title}
                        </h2>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-[10px] uppercase tracking-wider text-white/40 font-bold">
                          Total paid
                        </p>
                        <p className="text-xl font-black text-white">
                          ${Number(ticket.totalAmount || 0).toFixed(2)}
                        </p>
                      </div>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.06 + 0.14 }}
                      className="grid sm:grid-cols-2 gap-3"
                    >
                      <div className="flex items-start gap-2 text-sm text-white/60">
                        <Calendar className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                        <span>{ticket.dateLabel}</span>
                      </div>
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: idx * 0.06 + 0.16 }}
                        className="flex items-start gap-2 text-sm text-white/60"
                      >
                        <MapPin className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                        <span>{ticket.venue}</span>
                      </motion.div>
                      <div className="flex items-center gap-2 text-sm text-white/60">
                        <Ticket className="w-4 h-4 text-red-500 shrink-0" />
                        <span>
                          <strong className="text-white/80">Tier:</strong>{" "}
                          {ticket.tier}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-white/60">
                        <Hash className="w-4 h-4 text-red-500 shrink-0" />
                        <span>
                          <strong className="text-white/80">Qty:</strong>{" "}
                          {ticket.quantity}
                          {ticket.unitPrice > 0
                            ? ` · $${ticket.unitPrice} each`
                            : ""}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-white/60">
                        <CreditCard className="w-4 h-4 text-red-500 shrink-0" />
                        <span>
                          <strong className="text-white/80">Payment:</strong>{" "}
                          {ticket.payment || "—"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-white/60">
                        <span className="w-4 h-4 shrink-0" />
                        <span>
                          <strong className="text-white/80">Order:</strong>{" "}
                          {ticket.orderNumber}
                        </span>
                      </div>
                    </motion.div>

                    {qrByOrder[String(ticket.order_Id)] && (
                      <OrderQrTickets tickets={qrByOrder[String(ticket.order_Id)]} />
                    )}

                    {ticket.status && (
                      <p className="text-xs text-white/40">
                        Status:{" "}
                        <span className="text-white/60 font-semibold capitalize">
                          {ticket.status}
                        </span>
                      </p>
                    )}

                    <motion.div
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.06 + 0.2 }}
                      className="flex flex-wrap gap-2 pt-4 border-t border-[#1F2937]"
                    >
                      {ticket.eventId ? (
                        <Link
                          to={`/tickets/group.details/${ticket.eventId}`}
                          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-600 text-white text-xs font-black uppercase hover:bg-red-700 transition-colors"
                        >
                          <Ticket className="w-3.5 h-3.5" />
                          View event
                        </Link>
                      ) : (
                        <Link
                          to="/tickets"
                          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-600 text-white text-xs font-black uppercase hover:bg-red-700 transition-colors"
                        >
                          <Ticket className="w-3.5 h-3.5" />
                          Browse events
                        </Link>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDownloadInvoice(ticket)}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#1F2937] text-white/70 text-xs font-black uppercase hover:bg-white/10 hover:text-white transition-colors"
                      >
                        <Download className="w-3.5 h-3.5" />
                        Download invoice
                      </button>
                    </motion.div>
                  </motion.div>
                </motion.div>
              </motion.article>
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-20 bg-[#111827] rounded-2xl border border-[#1F2937]"
          >
            <Ticket className="w-10 h-10 text-white/20 mx-auto mb-3" />
            <p className="text-white/50 text-sm font-medium">
              No {activeTab} tickets found
            </p>
            <p className="text-white/30 text-xs mt-2 max-w-sm mx-auto">
              {search
                ? "Try a different search term or clear the search box."
                : "When you purchase tickets, they will appear here."}
            </p>
            {!search && (
              <Link
                to="/tickets"
                className="inline-block mt-6 text-sm text-red-500 font-semibold hover:underline"
              >
                Find events →
              </Link>
            )}
          </motion.div>
        )}
      </motion.div>

      <MarketplaceNewUIFooter />
    </div>
  );
}

export default function MyTicketsPage() {
  return (
    <UserAuth>
      <MyTicketsContent />
    </UserAuth>
  );
}
